import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "RECRUITER" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created!");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-white p-6">
      <Card className="w-full max-w-md p-8 border shadow-sm">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <Logo variant="mark-dark" size={32} />
          <span className="font-bold text-lg">alfredHR</span>
        </Link>
        <h2 className="text-2xl font-bold" data-testid="signup-title">Create your workspace</h2>
        <p className="text-sm text-neutral-500 mt-1 mb-6">Start hiring smarter in 30 seconds.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input data-testid="signup-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="mt-1.5" />
          </div>
          <div>
            <Label>Work email</Label>
            <Input data-testid="signup-email-input" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="mt-1.5" />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="signup-password-input" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={8} className="mt-1.5" />
          </div>
          <div>
            <Label>I am a…</Label>
            <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
              <SelectTrigger className="mt-1.5" data-testid="signup-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SUPER_ADMIN">Company Super Admin</SelectItem>
                <SelectItem value="HR_ADMIN">HR Admin</SelectItem>
                <SelectItem value="RECRUITER">Recruiter</SelectItem>
                <SelectItem value="HIRING_MANAGER">Hiring Manager</SelectItem>
                <SelectItem value="CANDIDATE">Candidate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" data-testid="signup-submit-btn" disabled={busy} className="btn-primary w-full rounded-full py-6">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
        <div className="text-sm text-center mt-6 text-neutral-500">
          Already have one? <Link to="/login" className="text-pink font-semibold">Sign in</Link>
        </div>
      </Card>
    </div>
  );
}
